import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import type { Control, UseFormGetValues, UseFormSetValue } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Button,
  ComboBox,
  Description,
  FieldError,
  Header,
  Input,
  Label,
  ListBox,
  ListBoxItem,
} from '@heroui/react'

import { FormModal } from '@/components/ui/form-modal'
import { FormSection, FormSectionEmpty } from '@/components/ui/form-section'
import {
  NumberFieldControl,
  SelectFieldControl,
  SwitchFieldControl,
  TextFieldControl,
} from '@/components/ui/form-fields'
import { PlusIcon, TrashIcon } from '@/components/ui/icons'
import { PAGE_LIMIT_MAX } from '@/lib/api/common'

import { fetchStockItems } from '../api'
import {
  findMenuChoice,
  inventoryKey,
  useItemRecipes,
  useMenuChoices,
  useRecipeModifierActions,
  useStockItems,
  useStockItemsById,
  useUnitCatalog,
} from '../hooks'
import type { MenuChoice, MenuChoiceGroup } from '../hooks'
import { ConstructionType, RecipeStatus, StockItemType } from '../types'
import type { StockItem, StockItemQuery, UnitOfMeasure } from '../types'
import type {
  CreateRecipeModifierBody,
  Recipe,
  RecipeModifier,
  RecipeModifierComponent,
  RecipeModifierComponentInput,
  RecipeModifierMode,
  UpdateRecipeModifierBody,
} from '../types'
import { modifierModeLabels, recipeQuantity, unitsLike } from './inventory-format'

/** Component quantities take the same fourth decimal a recipe line does. */
const QUANTITY_FORMAT: Intl.NumberFormatOptions = { maximumFractionDigits: 4 }

/** What can be put in: the first page of INGREDIENT items, as large as the API allows. */
const CANDIDATE_QUERY = {
  limit: PAGE_LIMIT_MAX,
  offset: 0,
  category: StockItemType.Ingredient,
}

/** The items recipes make, named from the stock list's first page — the Recipes tab's own. */
const RECIPE_ITEMS_QUERY = { limit: PAGE_LIMIT_MAX, offset: 0 }

/** How many recipes the Recipe box lists at once. */
const RECIPE_MATCH_LIMIT = 5

/**
 * Items a typed recipe search asks inventory for. Only made-from-other-items
 * ones have a recipe, so more are asked for than are shown.
 */
const RECIPE_ITEM_SEARCH_LIMIT = 20

function recipeItemSearch(search: string): StockItemQuery {
  return { limit: RECIPE_ITEM_SEARCH_LIMIT, offset: 0, search }
}

/** How many matches a typed search lists, and how long typing waits before asking. */
const MATCH_LIMIT = 10
const SEARCH_DEBOUNCE_MS = 300

/** Ingredients whose name, SKU or barcode hold the text — the box's search, or a scanned code. */
function matchQuery(search: string): StockItemQuery {
  return { limit: MATCH_LIMIT, offset: 0, category: StockItemType.Ingredient, search }
}

/**
 * Where a component's amount comes from. `BASE` is the API's `BASE_COMPONENT`:
 * the recipe's own amount for something taken out, the amount taken out for the
 * one thing a swap puts in. `FIXED` is a number typed here.
 */
type Amount = 'BASE' | 'FIXED'

/* No rule on the ingredient here: both lists always hold a row, and the one the
   mode hides would fail the form with an error nobody can see. The refinement
   below checks only the rows on screen. */
const componentSchema = z.object({
  stockItemId: z.string(),
  amount: z.enum(['BASE', 'FIXED']),
  quantity: z.number().optional(),
  unitOfMeasure: z.string().optional(),
})

type ComponentValues = z.infer<typeof componentSchema>

/**
 * The service's rules, checked before the request rather than after it, and
 * pinned to the field they are about — see `assertComposition` on the server.
 */
const schema = z
  .object({
    recipeId: z.string().min(1, 'Choose a recipe.'),
    name: z.string().trim().min(1, 'Give the modifier a name.').max(100, 'At most 100 characters.'),
    /** The linked menu choice's id, or `NOT_LINKED`. */
    menuChoiceId: z.string(),
    affectsStock: z.boolean(),
    mode: z.enum(['ADD', 'REMOVE', 'REPLACE']),
    removes: z.array(componentSchema),
    adds: z.array(componentSchema),
  })
  .superRefine((values, context) => {
    if (!values.affectsStock) return

    const removes = values.mode === 'ADD' ? [] : values.removes
    const adds = values.mode === 'REMOVE' ? [] : values.adds

    if (values.mode !== 'ADD' && removes.length === 0) {
      context.addIssue({ code: 'custom', path: ['removes'], message: 'Choose what it takes out.' })
    }
    if (values.mode !== 'REMOVE' && adds.length === 0) {
      context.addIssue({ code: 'custom', path: ['adds'], message: 'Choose what it puts in.' })
    }

    for (const [list, rows] of [
      ['removes', removes],
      ['adds', adds],
    ] as const) {
      rows.forEach((row, index) => {
        if (!row.stockItemId) {
          context.addIssue({
            code: 'custom',
            path: [list, index, 'stockItemId'],
            message: 'Choose an ingredient.',
          })
        }
        if (row.amount !== 'FIXED') return
        if (!row.quantity || row.quantity <= 0) {
          context.addIssue({
            code: 'custom',
            path: [list, index, 'quantity'],
            message: 'Enter an amount more than 0.',
          })
        }
        if (!row.unitOfMeasure) {
          context.addIssue({
            code: 'custom',
            path: [list, index, 'unitOfMeasure'],
            message: 'Choose a unit.',
          })
        }
      })

      const ids = rows.map((row) => row.stockItemId).filter(Boolean)
      if (new Set(ids).size !== ids.length) {
        context.addIssue({
          code: 'custom',
          path: [list],
          message: 'Each ingredient can only be listed once.',
        })
      }
    }

    // Only a swap's one removal at the recipe's amount has an amount to copy.
    const copying = adds.filter((row) => row.amount === 'BASE')
    if (
      copying.length > 0 &&
      !(copying.length === 1 && removes.length === 1 && removes[0].amount === 'BASE')
    ) {
      context.addIssue({
        code: 'custom',
        path: ['adds'],
        message:
          '“Same amount” copies what one ingredient taken out at the recipe’s amount used. Use a set amount instead.',
      })
    }

    if (adds.some((add) => removes.some((remove) => remove.stockItemId === add.stockItemId))) {
      context.addIssue({
        code: 'custom',
        path: ['adds'],
        message: 'An ingredient cannot be both taken out and put in.',
      })
    }
  })

type FormValues = z.infer<typeof schema>

const NEW_REMOVE: ComponentValues = { stockItemId: '', amount: 'BASE' }
const NEW_ADD: ComponentValues = { stockItemId: '', amount: 'FIXED' }

/**
 * The picker's "no link" key. Not `''`: React Aria reads an empty key as no
 * selection, which would blank the trigger instead of saying "Not linked".
 */
const NOT_LINKED = 'not-linked'

/** Lines for no recipe at all, held once so the memos that read them stay put. */
const NO_LINES: Recipe['lines'] = []

/** A saved component back as a form row. A `BASE_COMPONENT` one types no amount. */
function toRow(component: RecipeModifierComponent): ComponentValues {
  return component.quantityMode === 'FIXED'
    ? {
        stockItemId: component.stockItemId,
        amount: 'FIXED',
        quantity: component.quantity,
        unitOfMeasure: component.unitOfMeasure,
      }
    : { stockItemId: component.stockItemId, amount: 'BASE' }
}

/** The form as a modifier fills it, or empty for a new one on `recipeId`. */
function initialValues(modifier: RecipeModifier | null, recipeId: string): FormValues {
  if (!modifier) {
    return {
      recipeId,
      name: '',
      menuChoiceId: NOT_LINKED,
      affectsStock: false,
      mode: 'ADD',
      removes: [NEW_REMOVE],
      adds: [NEW_ADD],
    }
  }

  const rowsOf = (effectType: 'ADD' | 'REMOVE') =>
    modifier.components.filter((part) => part.effectType === effectType).map(toRow)
  const removes = rowsOf('REMOVE')
  const adds = rowsOf('ADD')

  return {
    recipeId: modifier.recipeId,
    name: modifier.name,
    menuChoiceId: modifier.catalogModifierRef ?? NOT_LINKED,
    affectsStock: modifier.affectsStock,
    mode: modifier.mode ?? 'ADD',
    removes: removes.length ? removes : [NEW_REMOVE],
    adds: adds.length ? adds : [NEW_ADD],
  }
}

/** The request's component, from a row and the side it sits on. */
function toComponent(
  row: ComponentValues,
  effectType: 'ADD' | 'REMOVE',
): RecipeModifierComponentInput {
  return row.amount === 'BASE'
    ? { stockItemId: row.stockItemId, effectType, quantityMode: 'BASE_COMPONENT' }
    : {
        stockItemId: row.stockItemId,
        effectType,
        quantityMode: 'FIXED',
        quantity: row.quantity as number,
        unitOfMeasure: row.unitOfMeasure as UnitOfMeasure,
      }
}

/** An ingredient as the pickers offer it: its name, its unit, a hint, and the codes it is found by. */
interface IngredientOption {
  id: string
  label: string
  hint: string
  unit: UnitOfMeasure
  sku: string
  barcode: string | null
}

function toOption(
  item: StockItem,
  hint = [item.sku, item.barcode, item.unitOfMeasure].filter(Boolean).join(' · '),
): IngredientOption {
  return {
    id: item.id,
    label: item.name,
    hint,
    unit: item.unitOfMeasure,
    sku: item.sku,
    barcode: item.barcode,
  }
}

/** Typed text against an option: any part of its name, SKU or barcode, ignoring case. */
function matchesText(option: IngredientOption, text: string): boolean {
  const needle = text.toLowerCase()
  return [option.label, option.sku, option.barcode ?? ''].some((field) =>
    field.toLowerCase().includes(needle),
  )
}

/** A scanned or typed code against an option: its barcode or SKU, whole. */
function isCodeOf(option: IngredientOption, code: string): boolean {
  const wanted = code.toLowerCase()
  return option.barcode?.toLowerCase() === wanted || option.sku.toLowerCase() === wanted
}

interface RecipeModifierFormDialogProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  /** The recipes the modifier can go on. */
  recipes: Recipe[]
  /** The recipe the form opens on. */
  defaultRecipeId: string
  /** The modifier to edit, or `null` for a new one. */
  modifier?: RecipeModifier | null
  /** Told which recipe the modifier was saved to. */
  onSaved: (recipeId: string) => void
}

/**
 * A new modifier on a recipe — a choice a customer makes, and what it does to
 * the stock a sale of that recipe uses.
 *
 * Most choices change nothing on the shelf ("no ice", "extra hot"), so stock is
 * off by default and the form is a name. Switched on, the mode decides what the
 * rest asks for: what it takes out of the recipe, what it puts in, or both for a
 * swap. What it takes out can only be the recipe's own ingredients, and by
 * default at the recipe's amount — "no milk" removes exactly the milk the recipe
 * uses. What a swap puts in can copy that amount, so oat for dairy is one choice
 * per side and no arithmetic.
 *
 * A new modifier's recipe can be changed here. What it takes out is the old
 * recipe's ingredients, so changing it starts that list over. A saved modifier
 * stays on its recipe: the service has no move.
 *
 * The menu choice is what lets a sale find it. The POS sends the id of each
 * choice picked on a line, and only a modifier linked to that choice is applied
 * — an unlinked one is kept but never used.
 */
export function RecipeModifierFormDialog({
  isOpen,
  onOpenChange,
  recipes,
  defaultRecipeId,
  modifier = null,
  onSaved,
}: RecipeModifierFormDialogProps) {
  const { create, update } = useRecipeModifierActions()
  const save = modifier ? update : create
  const isEditing = modifier !== null

  /* One idempotency key per distinct body, as on the other create forms. */
  const [attempt, setAttempt] = useState<{ key: string; body: string } | null>(null)

  const { control, handleSubmit, setValue, getValues, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: initialValues(modifier, defaultRecipeId),
  })

  const { groups: menuGroups, isLoading: isLoadingMenu } = useMenuChoices()

  const recipeId = useWatch({ control, name: 'recipeId' })
  const affectsStock = useWatch({ control, name: 'affectsStock' })
  const mode = useWatch({ control, name: 'mode' })

  const removes = useFieldArray({ control, name: 'removes' })
  const adds = useFieldArray({ control, name: 'adds' })

  /* A recipe the Recipe box found by searching inventory is not among `recipes`. */
  const [searchedRecipe, setSearchedRecipe] = useState<Recipe | null>(null)
  const recipe =
    recipes.find((candidate) => candidate.id === recipeId) ??
    (searchedRecipe?.id === recipeId ? searchedRecipe : null)
  const lines = recipe?.lines ?? NO_LINES

  /* A new recipe has other ingredients to take out, so that list starts over.
     What goes in stays, but for the new recipe's own item, which cannot. */
  function changeRecipe(next: Recipe) {
    setSearchedRecipe(next)
    removes.replace([NEW_REMOVE])
    adds.replace(
      getValues('adds').map((row) => (row.stockItemId === next.stockItemId ? NEW_ADD : row)),
    )
  }

  /* What can be taken out: the recipe's own ingredients, nothing else. */
  const baseItems = useStockItemsById(lines.map((line) => line.itemId))
  const removeOptions = useMemo<IngredientOption[]>(
    () =>
      lines.flatMap((line) => {
        const item = baseItems.get(line.itemId)
        return item
          ? [toOption(item, `${recipeQuantity(line.qty)} ${line.unitOfMeasure} in the recipe`)]
          : []
      }),
    [lines, baseItems],
  )

  /* What can be put in: any ingredient but the recipe's own item. The list
     leaves archived items out already, which the service would refuse.

     Plus what a saved modifier already puts in, read by id: it may be an item
     of another type — packaging — from before this list was narrowed, and
     without it in the options its select would open blank. */
  const { items: candidates } = useStockItems(CANDIDATE_QUERY)
  const savedAddIds = useMemo(
    () =>
      (modifier?.components ?? [])
        .filter((part) => part.effectType === 'ADD')
        .map((part) => part.stockItemId),
    [modifier],
  )
  const savedAdds = useStockItemsById(savedAddIds)
  const addOptions = useMemo<IngredientOption[]>(
    () =>
      [
        ...new Map<string, StockItem>([
          ...[...savedAdds.values()].map((item) => [item.id, item] as const),
          ...candidates.map((item) => [item.id, item] as const),
        ]).values(),
      ]
        .filter((item) => item.id !== recipe?.stockItemId)
        .map((item) => toOption(item)),
    [candidates, savedAdds, recipe],
  )

  const submit = handleSubmit(async (values) => {
    const menuChoiceId = values.menuChoiceId === NOT_LINKED ? null : values.menuChoiceId
    const stock: Pick<CreateRecipeModifierBody, 'affectsStock' | 'mode' | 'components'> = {
      affectsStock: values.affectsStock,
    }

    // Only the side the mode uses is sent; the other is whatever was left in the
    // form before the mode changed. Sent in full on every save: an edit that
    // changes the mode must send components, and one that does not and sends
    // the same ones changes nothing.
    if (values.affectsStock) {
      stock.mode = values.mode
      stock.components = [
        ...(values.mode === 'ADD' ? [] : values.removes.map((row) => toComponent(row, 'REMOVE'))),
        ...(values.mode === 'REMOVE' ? [] : values.adds.map((row) => toComponent(row, 'ADD'))),
      ]
    }

    if (modifier) {
      // `null` unlinks; the service takes it as a change only when there was a link.
      const body: UpdateRecipeModifierBody = {
        name: values.name.trim(),
        catalogModifierRef: menuChoiceId,
        ...stock,
      }
      await update.mutateAsync({ recipeId: modifier.recipeId, modifierId: modifier.id, body })
      onSaved(modifier.recipeId)
      return
    }

    const body: CreateRecipeModifierBody = {
      name: values.name.trim(),
      ...(menuChoiceId ? { catalogModifierRef: menuChoiceId } : {}),
      ...stock,
    }

    // The same body on another recipe is another modifier, so the recipe is part of the key.
    const serialised = JSON.stringify({ recipeId: values.recipeId, body })
    const next =
      attempt?.body === serialised ? attempt : { key: crypto.randomUUID(), body: serialised }
    setAttempt(next)

    await create.mutateAsync({ recipeId: values.recipeId, body, idempotencyKey: next.key })
    onSaved(values.recipeId)
  })

  /* A link the menu no longer lists — its choice was deleted, or the ref was
     typed by hand before there was a picker — stays selectable, so opening the
     form does not quietly drop it. */
  const savedRef = modifier?.catalogModifierRef ?? null
  const isSavedRefMissing =
    savedRef !== null && !isLoadingMenu && findMenuChoice(menuGroups, savedRef) === null

  const takesOut = affectsStock && mode !== 'ADD'
  const putsIn = affectsStock && mode !== 'REMOVE'

  return (
    <FormModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={isEditing ? 'Edit modifier' : 'New modifier'}
      description={`A choice on ${recipe?.name ?? 'a recipe'}, and what it does to the stock a sale uses.`}
      submitLabel={isEditing ? 'Save changes' : 'Add modifier'}
      onSubmit={submit}
      isBusy={save.isPending}
      error={save.error}
      errorFallback={isEditing ? 'Could not save this modifier.' : 'Could not add this modifier.'}
      dialogClassName="max-w-4xl"
      scroll="inside"
    >
      <FormSection title="The modifier">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextFieldControl
            control={control}
            name="name"
            label="Name"
            placeholder="Oat milk, Extra shot…"
            isRequired
            autoFocus
          />
          <RecipeField
            control={control}
            recipes={recipes}
            isDisabled={isEditing}
            onChange={changeRecipe}
          />
          <div className="sm:col-span-2">
            <MenuChoiceField
              control={control}
              groups={menuGroups}
              isLoading={isLoadingMenu}
              missingRef={isSavedRefMissing ? savedRef : null}
              onChoose={(choice) => {
                // A blank name takes the choice's own, which is what it is called on the menu.
                if (!getValues('name').trim()) {
                  setValue('name', choice.name, { shouldValidate: true })
                }
              }}
            />
          </div>
        </div>
      </FormSection>

      <FormSection title="Stock">
        <div className="flex flex-col gap-4">
          <SwitchFieldControl
            control={control}
            name="affectsStock"
            label="Changes the stock a sale uses"
            description="Leave off for a choice that changes nothing on the shelf — no ice, extra hot."
          />

          {affectsStock && (
            <SelectFieldControl
              control={control}
              name="mode"
              label="What it does"
              isRequired
              options={(Object.keys(modifierModeLabels) as RecipeModifierMode[]).map((value) => ({
                value,
                label: modifierModeLabels[value],
              }))}
            />
          )}
        </div>
      </FormSection>

      {takesOut && (
        <FormSection
          title="Takes out"
          description="Only the recipe's own ingredients can be taken out."
          action={
            removeOptions.length > 0 && (
              <AddRowButton label="Add ingredient" onPress={() => removes.append(NEW_REMOVE)} />
            )
          }
          alert={
            <ListError
              message={
                // With nothing to choose there is no row on screen to hold the
                // error, so it is said here or not at all.
                removeOptions.length === 0 && formState.errors.removes
                  ? 'Nothing to take out: this recipe has no ingredients yet.'
                  : listError(formState.errors.removes)
              }
            />
          }
        >
          {removeOptions.length === 0 ? (
            <FormSectionEmpty>
              This recipe has no ingredients yet, so there is nothing to take out. Add them to the
              recipe first.
            </FormSectionEmpty>
          ) : (
            <div className="flex flex-col gap-3">
              {removes.fields.map((field, index) => (
                <ComponentRow
                  key={field.id}
                  list="removes"
                  index={index}
                  control={control}
                  setValue={setValue}
                  getValues={getValues}
                  options={removeOptions}
                  source="recipe"
                  amountOptions={[
                    { value: 'BASE', label: "The recipe's amount" },
                    { value: 'FIXED', label: 'A set amount' },
                  ]}
                  canRemove={removes.fields.length > 1}
                  onRemove={() => removes.remove(index)}
                />
              ))}
            </div>
          )}
        </FormSection>
      )}

      {putsIn && (
        <FormSection
          title="Puts in"
          description={
            mode === 'REPLACE'
              ? 'What goes in instead. "Same amount" uses exactly what was taken out.'
              : 'What the choice adds to the recipe.'
          }
          action={<AddRowButton label="Add ingredient" onPress={() => adds.append(NEW_ADD)} />}
          alert={<ListError message={listError(formState.errors.adds)} />}
        >
          <div className="flex flex-col gap-3">
            {adds.fields.map((field, index) => (
              <ComponentRow
                key={field.id}
                list="adds"
                index={index}
                control={control}
                setValue={setValue}
                getValues={getValues}
                options={addOptions}
                source="inventory"
                excludeId={recipe?.stockItemId}
                amountOptions={[
                  { value: 'FIXED', label: 'A set amount' },
                  ...(mode === 'REPLACE'
                    ? [{ value: 'BASE' as const, label: 'Same amount as taken out' }]
                    : []),
                ]}
                canRemove={adds.fields.length > 1}
                onRemove={() => adds.remove(index)}
              />
            ))}
          </div>
        </FormSection>
      )}
    </FormModal>
  )
}

/** A recipe as the picker offers it: its name, and what tells two of them apart. */
interface RecipeOption {
  id: string
  label: string
  /** `Makes Latte · LAT-01 · 3 ingredients`. */
  hint: string
  /** The recipe's name, and its item's name, SKU and barcode — what a search reads. */
  searchable: string[]
}

function toRecipeOption(recipe: Recipe, item: StockItem | undefined): RecipeOption {
  const lineCount = recipe.lines.length
  const hint = [
    item ? `Makes ${item.name}` : null,
    item?.sku,
    recipe.status === RecipeStatus.Draft || lineCount === 0
      ? 'No ingredients yet'
      : `${lineCount} ingredient${lineCount === 1 ? '' : 's'}`,
    recipe.status === RecipeStatus.Inactive ? 'Inactive' : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return {
    id: recipe.id,
    label: recipe.name,
    hint,
    searchable: [recipe.name, item?.name ?? '', item?.sku ?? '', item?.barcode ?? ''],
  }
}

interface RecipeFieldProps {
  control: Control<FormValues>
  recipes: Recipe[]
  isDisabled: boolean
  /** Told of a different recipe, so the form can start its take-outs over. */
  onChange: (recipe: Recipe) => void
}

/**
 * The recipe a modifier goes on, searched by typing. Recipe names are often the
 * item's own, so each option also says what it makes, that item's SKU and how
 * many ingredients it has — two "Latte" recipes stop looking alike.
 *
 * The search is live. The loaded recipes (the first hundred) are matched at
 * once on the recipe's name and its item's name, SKU and barcode. Inventory is
 * then searched for items by name, SKU or barcode, and the recipes of the
 * matching made-from-other-items ones join the list — so a recipe past the
 * first hundred is still found. The recipes API has no search of its own, so a
 * recipe past those is found by its item, not by a name of its own.
 */
function RecipeField({ control, recipes, isDisabled, onChange }: RecipeFieldProps) {
  const [search, setSearch] = useState('')
  const [searchText, setSearchText] = useState('')
  /* A recipe found by the inventory search is in no list the parent holds, so
     it is kept here, or the box would blank once the search moves on. */
  const [picked, setPicked] = useState<RecipeOption | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setSearchText(search.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  /* The items the recipes make. Most are on the stock list's first page, which
     the Recipes tab reads too; only the ones past it are read by id. */
  const { items: stockPage } = useStockItems(RECIPE_ITEMS_QUERY)
  const missingIds = useMemo(() => {
    const onPage = new Set(stockPage.map((item) => item.id))
    return [...new Set(recipes.map((recipe) => recipe.stockItemId))].filter(
      (id) => !onPage.has(id),
    )
  }, [recipes, stockPage])
  const missing = useStockItemsById(missingIds)

  const options = useMemo(() => {
    const byId = new Map<string, StockItem>(stockPage.map((item) => [item.id, item]))
    return recipes.map((recipe) =>
      toRecipeOption(recipe, byId.get(recipe.stockItemId) ?? missing.get(recipe.stockItemId)),
    )
  }, [recipes, stockPage, missing])

  /* The chosen recipe's name in the box is not a search for it, so the full
     list comes back on reopening. */
  const recipeId = useWatch({ control, name: 'recipeId' })
  const selectedLabel = (
    options.find((option) => option.id === recipeId) ??
    (picked?.id === recipeId ? picked : undefined)
  )?.label
  const remoteTerm = searchText === selectedLabel ? '' : searchText
  const itemQuery = useMemo(() => recipeItemSearch(remoteTerm), [remoteTerm])
  // Held back with nothing typed: the service answers `search=` with a 400.
  const {
    items: matchedItems,
    isLoading: isLoadingItems,
    isRefreshing: isRefreshingItems,
  } = useStockItems(itemQuery, { enabled: Boolean(remoteTerm) })

  /* Only the made-from-other-items matches have a recipe; the ones already
     loaded need no second read. */
  const loadedItemIds = useMemo(
    () => new Set(recipes.map((recipe) => recipe.stockItemId)),
    [recipes],
  )
  const composites = useMemo(
    () =>
      remoteTerm
        ? matchedItems
            .filter(
              (item) =>
                item.constructionType === ConstructionType.Composite &&
                !loadedItemIds.has(item.id),
            )
            .slice(0, RECIPE_MATCH_LIMIT)
        : [],
    [remoteTerm, matchedItems, loadedItemIds],
  )
  const { recipes: foundRecipes, isLoading: isLoadingRecipes } = useItemRecipes(
    useMemo(() => composites.map((item) => item.id), [composites]),
  )
  const remoteOptions = useMemo(
    () =>
      composites.flatMap((item) => {
        const recipe = foundRecipes.get(item.id)
        return recipe ? [{ option: toRecipeOption(recipe, item), recipe }] : []
      }),
    [composites, foundRecipes],
  )
  const isSearching =
    Boolean(remoteTerm) &&
    (isLoadingItems || isRefreshingItems || (composites.length > 0 && isLoadingRecipes))

  return (
    <Controller
      control={control}
      name="recipeId"
      render={({ field, fieldState }) => {
        const selected =
          options.find((option) => option.id === field.value) ??
          (picked?.id === field.value ? picked : undefined)
        const term = search.trim() === selected?.label ? '' : search.trim().toLowerCase()
        const local = term
          ? options.filter((option) =>
              option.searchable.some((text) => text.toLowerCase().includes(term)),
            )
          : options
        // The inventory search answers for what was typed a moment ago; it joins
        // only while that is still what the box says.
        const remote =
          term && remoteTerm.toLowerCase() === term
            ? remoteOptions
                .map((found) => found.option)
                .filter((option) => !local.some((other) => other.id === option.id))
            : []
        // A short list; typing narrows it. The chosen recipe stays in it, or the box would blank.
        const firstFew = [...local, ...remote].slice(0, RECIPE_MATCH_LIMIT)
        const shown =
          selected && !firstFew.some((option) => option.id === selected.id)
            ? [selected, ...firstFew.slice(0, RECIPE_MATCH_LIMIT - 1)]
            : firstFew

        return (
          <ComboBox
            value={field.value || null}
            onChange={(key) => {
              const id = String(key)
              const choice =
                recipes.find((candidate) => candidate.id === id) ??
                remoteOptions.find((found) => found.recipe.id === id)?.recipe
              // Emptying the box keeps the recipe: a modifier always has one.
              if (!choice || choice.id === field.value) return
              setPicked(shown.find((option) => option.id === id) ?? null)
              field.onChange(choice.id)
              onChange(choice)
            }}
            onInputChange={setSearch}
            // Filtered above on the item's name and codes as well as the recipe's.
            defaultFilter={() => true}
            allowsEmptyCollection
            onBlur={field.onBlur}
            isInvalid={fieldState.invalid}
            isRequired
            isDisabled={isDisabled}
            fullWidth
            className="flex flex-col gap-1.5"
          >
            <Label>Recipe</Label>
            <ComboBox.InputGroup>
              <Input ref={field.ref} placeholder="Search recipes…" />
              <ComboBox.Trigger />
            </ComboBox.InputGroup>
            <ComboBox.Popover>
              <ListBox
                renderEmptyState={() => (
                  <p className="text-muted px-3 py-2 text-xs">
                    {isSearching ? 'Searching…' : `No recipe matches “${search.trim()}”.`}
                  </p>
                )}
              >
                {shown.map((option) => (
                  // The name alone, since it is what the box shows once chosen.
                  <ListBoxItem key={option.id} id={option.id} textValue={option.label}>
                    <span className="flex flex-col">
                      <span>{option.label}</span>
                      <span className="text-muted text-xs">{option.hint}</span>
                    </span>
                  </ListBoxItem>
                ))}
              </ListBox>
            </ComboBox.Popover>
            {selected && !fieldState.error ? (
              <Description>{selected.hint}</Description>
            ) : (
              <FieldError>{fieldState.error?.message}</FieldError>
            )}
          </ComboBox>
        )
      }}
    />
  )
}

interface MenuChoiceFieldProps {
  control: Control<FormValues>
  groups: MenuChoiceGroup[]
  isLoading: boolean
  /** A saved link the menu no longer lists, kept as an option so it is not lost. */
  missingRef: string | null
  onChoose: (choice: MenuChoice) => void
}

/**
 * The menu choice a modifier stands for, picked from the menu's choice groups.
 * "Not linked" is a real answer — a modifier can be set up before its choice —
 * but the description says what it costs.
 *
 * Searched by typing: each choice is matched on its group and its own name, so
 * "milk" finds every choice in a Milk group as well as "Oat milk" anywhere else.
 * Emptying the box unlinks.
 */
function MenuChoiceField({
  control,
  groups,
  isLoading,
  missingRef,
  onChoose,
}: MenuChoiceFieldProps) {
  return (
    <Controller
      control={control}
      name="menuChoiceId"
      render={({ field }) => (
        <ComboBox
          value={field.value}
          onChange={(key) => {
            const id = key === null ? NOT_LINKED : String(key)
            field.onChange(id)
            const found = findMenuChoice(groups, id)
            if (found) onChoose(found.choice)
          }}
          allowsEmptyCollection
          onBlur={field.onBlur}
          isDisabled={isLoading}
          fullWidth
          className="flex flex-col gap-1.5"
        >
          <Label>Menu choice</Label>
          <ComboBox.InputGroup>
            <Input
              ref={field.ref}
              placeholder={isLoading ? 'Loading the menu…' : 'Search choices…'}
            />
            <ComboBox.Trigger />
          </ComboBox.InputGroup>
          <ComboBox.Popover>
            <ListBox
              renderEmptyState={() => (
                <p className="text-muted px-3 py-2 text-xs">No menu choice matches.</p>
              )}
            >
              <ListBox.Section id="none">
                <ListBoxItem id={NOT_LINKED} textValue="Not linked">
                  <span className="text-muted">Not linked</span>
                </ListBoxItem>
                {missingRef && (
                  <ListBoxItem id={missingRef} textValue={`No longer on the menu (${missingRef})`}>
                    No longer on the menu ({missingRef})
                  </ListBoxItem>
                )}
              </ListBox.Section>
              {groups.map((group) => (
                <ListBox.Section key={group.id} id={group.id}>
                  <Header>{group.name}</Header>
                  {group.choices.map((choice) => (
                    <ListBoxItem
                      key={choice.id}
                      id={choice.id}
                      textValue={`${group.name}: ${choice.name}`}
                    >
                      {choice.name}
                    </ListBoxItem>
                  ))}
                </ListBox.Section>
              ))}
            </ListBox>
          </ComboBox.Popover>
          <Description>
            The choice on the menu this modifier stands for. A sale only applies a modifier that is
            linked to the choice the customer picked.
          </Description>
        </ComboBox>
      )}
    />
  )
}

/** A list-level error — wrong count, a repeat — wherever react-hook-form put it. */
function listError(
  error: { message?: string; root?: { message?: string } } | undefined,
): string | undefined {
  return error?.root?.message ?? error?.message
}

function ListError({ message }: { message?: string }) {
  if (!message) return null

  return (
    <p role="alert" className="text-danger mt-2 text-xs">
      {message}
    </p>
  )
}

function AddRowButton({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className="border-border h-8 rounded-full border px-3 text-xs"
      onPress={onPress}
    >
      <PlusIcon width={14} height={14} />
      {label}
    </Button>
  )
}

interface ComponentRowProps {
  list: 'removes' | 'adds'
  index: number
  control: Control<FormValues>
  setValue: UseFormSetValue<FormValues>
  getValues: UseFormGetValues<FormValues>
  /** What the list offers before anything is typed — and, for `recipe`, all it can offer. */
  options: IngredientOption[]
  /**
   * Where a search looks: among `options` (the recipe's own ingredients), or
   * across every ingredient in inventory.
   */
  source: 'recipe' | 'inventory'
  /** An item a search must never offer — the recipe's own. */
  excludeId?: string
  amountOptions: { value: Amount; label: string }[]
  canRemove: boolean
  onRemove: () => void
}

/**
 * One ingredient a modifier touches: which, and how much.
 *
 * The ingredient is searched for — by name, SKU or barcode — or scanned. A
 * scanner types its code and presses Enter, so Enter on a code that is exactly
 * one item's barcode or SKU picks that item; an inventory search waits for its
 * answer first. Enter in the box never submits the form, which a scan ending in
 * the middle of a half-filled modifier otherwise would.
 *
 * Choosing the ingredient resets a set amount's unit to the ingredient's own —
 * the one unit certain to pass — and the unit list only offers units that
 * measure the same thing, so the form cannot build a component the API would
 * refuse.
 */
function ComponentRow({
  list,
  index,
  control,
  setValue,
  getValues,
  options,
  source,
  excludeId,
  amountOptions,
  canRemove,
  onRemove,
}: ComponentRowProps) {
  const ingredientId = useWatch({ control, name: `${list}.${index}.stockItemId` as const })
  const amount = useWatch({ control, name: `${list}.${index}.amount` as const })
  const { catalog: units } = useUnitCatalog()

  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [searchText, setSearchText] = useState('')
  /** Why the last code entered picked nothing, said under the box until the next keystroke. */
  const [notice, setNotice] = useState<string | null>(null)
  /* An item found by an inventory search is in no list the parent holds, so it
     is kept here — its unit is what the unit select is narrowed by. */
  const [picked, setPicked] = useState<IngredientOption | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setSearchText(search.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  const selected =
    (picked?.id === ingredientId ? picked : undefined) ??
    options.find((option) => option.id === ingredientId)
  const ingredientUnit = selected?.unit

  /* Picking an item writes its name into the box, which reads as a search for
     it — not something typed, so the full list comes back on reopening. */
  const term = searchText === selected?.label ? '' : searchText
  const searchesInventory = source === 'inventory' && Boolean(term)

  const query = useMemo(() => matchQuery(term), [term])
  // Held back with nothing typed: the service answers `search=` with a 400.
  const {
    items: matches,
    isLoading,
    isRefreshing,
  } = useStockItems(query, { enabled: searchesInventory })
  const isSearching = searchesInventory && (isLoading || isRefreshing)

  const shown = useMemo(() => {
    const found = !term
      ? options
      : source === 'recipe'
        ? options.filter((option) => matchesText(option, term))
        : matches.filter((match) => match.id !== excludeId).map((match) => toOption(match))
    // The chosen item stays listed, or the box would blank under it.
    return selected && !found.some((option) => option.id === selected.id)
      ? [selected, ...found]
      : found
  }, [term, source, options, matches, excludeId, selected])

  function choose(option: IngredientOption) {
    setPicked(option)
    setNotice(null)
    setValue(`${list}.${index}.stockItemId`, option.id, { shouldValidate: true })
    setValue(`${list}.${index}.unitOfMeasure`, option.unit, { shouldValidate: true })
  }

  /**
   * A code entered with Enter: the item whose barcode or SKU it is. The search
   * box may not have caught up with a scanner — its debounce outlasts the scan —
   * so inventory is asked for the code itself, sharing the list's cache.
   */
  async function lookUp(code: string) {
    const from = getValues(`${list}.${index}.stockItemId`)
    let candidates: IngredientOption[]
    try {
      candidates =
        source === 'recipe'
          ? options
          : (
              await queryClient.fetchQuery({
                queryKey: [...inventoryKey, 'items', matchQuery(code)],
                queryFn: () => fetchStockItems(matchQuery(code)),
              })
            ).rows
              .filter((item) => item.id !== excludeId)
              .map((item) => toOption(item))
    } catch {
      setNotice('Could not look that code up. Try again.')
      return
    }

    // Enter on a highlighted row picks it; that choice stands over the code.
    if (getValues(`${list}.${index}.stockItemId`) !== from) return

    const hit = candidates.find((option) => isCodeOf(option, code))
    if (hit) choose(hit)
    else
      setNotice(`No ingredient${source === 'recipe' ? ' in this recipe' : ''} has the code “${code}”.`)
  }

  /* An amount this side no longer offers — a swap's "same amount", after the mode
     moved off REPLACE — falls back to the first one it does. */
  const offered = amountOptions.some((option) => option.value === amount)
  useEffect(() => {
    if (!offered) setValue(`${list}.${index}.amount`, amountOptions[0].value)
  }, [offered, list, index, amountOptions, setValue])

  return (
    /* The quantity column holds the figure between its −/+ (40px each), so it is
       as wide as the recipe editor's; `minmax(0, …)` lets a long ingredient name
       truncate instead of pushing the row wider. */
    <div className="border-border grid gap-3 rounded-xl border p-3 sm:grid-cols-[minmax(0,1fr)_180px] lg:grid-cols-[minmax(0,1fr)_170px_10rem_7rem_2.75rem]">
      <Controller
        control={control}
        name={`${list}.${index}.stockItemId` as const}
        render={({ field, fieldState }) => (
          <ComboBox
            value={field.value || null}
            onChange={(key) => {
              const choice = shown.find((option) => option.id === String(key))
              if (choice) choose(choice)
              else field.onChange('')
            }}
            onInputChange={(text) => {
              setSearch(text)
              setNotice(null)
            }}
            // Already filtered on name, SKU and barcode; filtering again by the
            // box's text would drop the SKU and barcode matches.
            defaultFilter={() => true}
            allowsEmptyCollection
            onBlur={field.onBlur}
            isInvalid={fieldState.invalid}
            isRequired
            fullWidth
            className="flex flex-col gap-1.5"
          >
            <Label>Ingredient</Label>
            <ComboBox.InputGroup>
              <Input
                ref={field.ref}
                placeholder="Search"
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return
                  const code = search.trim()
                  if (!code || code === selected?.label) return
                  // A scanner's Enter, or a typed code: matched, never a submit.
                  event.preventDefault()
                  void lookUp(code)
                }}
              />
              <ComboBox.Trigger />
            </ComboBox.InputGroup>
            <ComboBox.Popover>
              <ListBox
                renderEmptyState={() => (
                  <p className="text-muted px-3 py-2 text-xs">
                    {isSearching
                      ? 'Searching…'
                      : term
                        ? `No ingredient matches “${term}”.`
                        : 'No ingredients yet. Add one on the Stock items tab.'}
                  </p>
                )}
              >
                {shown.map((option) => (
                  // The name alone, since it is what the box shows once chosen.
                  <ListBoxItem key={option.id} id={option.id} textValue={option.label}>
                    <span className="flex flex-col">
                      <span>{option.label}</span>
                      <span className="text-muted text-xs">{option.hint}</span>
                    </span>
                  </ListBoxItem>
                ))}
              </ListBox>
            </ComboBox.Popover>
            {notice && !fieldState.error ? (
              <p role="alert" className="text-danger text-xs">
                {notice}
              </p>
            ) : (
              <FieldError>{fieldState.error?.message}</FieldError>
            )}
          </ComboBox>
        )}
      />

      <SelectFieldControl
        control={control}
        name={`${list}.${index}.amount` as const}
        label="Amount"
        isRequired
        options={amountOptions}
      />

      {amount === 'FIXED' ? (
        <>
          <NumberFieldControl
            control={control}
            name={`${list}.${index}.quantity` as const}
            label="Quantity"
            minValue={0}
            formatOptions={QUANTITY_FORMAT}
            isRequired
          />
          <SelectFieldControl
            control={control}
            name={`${list}.${index}.unitOfMeasure` as const}
            label="Unit"
            isRequired
            isDisabled={!ingredientUnit}
            options={(ingredientUnit ? unitsLike(ingredientUnit, units) : []).map((unit) => ({
              value: unit,
              label: unit,
            }))}
          />
        </>
      ) : (
        /* Two empty cells, so the remove button keeps its column on wide screens. */
        <span className="hidden lg:col-span-2 lg:block" />
      )}

      <Button
        variant="ghost"
        aria-label="Remove ingredient"
        isDisabled={!canRemove}
        onPress={onRemove}
        className="text-muted hover:text-danger size-11 self-end rounded-full"
      >
        <TrashIcon width={16} height={16} />
      </Button>
    </div>
  )
}
